import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { storage, db } from './config';

// Upload a file to Firebase Storage
export const uploadFile = async (file, userId = null) => {
  try {
    console.log("Starting file upload...");
    
    // Create a unique file path
    const timestamp = new Date().getTime();
    const filePath = `uploads/${userId || 'guest'}/${timestamp}_${file.name}`;
    const storageRef = ref(storage, filePath);
    
    console.log(`Uploading to path: ${filePath}`);
    
    // Upload the file
    try {
      const snapshot = await uploadBytes(storageRef, file);
      console.log("File uploaded successfully");
      
      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log("Download URL obtained");
      
      // Get file extension to determine file type
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const fileType = getFileType(fileExtension);
      
      // Create file metadata in Firestore
      let fileData = {
        filename: file.name,
        filePath: filePath,
        downloadURL: downloadURL,
        fileType: fileType,
        fileSize: file.size,
        uploadedAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 48 * 60 * 60 * 1000) // 48 hours from now
      };
      
      let fileId;
      
      if (userId) {
        // For authenticated users, add userId
        fileData.userId = userId;
        
        // Add to Firestore
        try {
          const docRef = await addDoc(collection(db, "files"), fileData);
          fileId = docRef.id;
          console.log("File metadata saved to Firestore");
        } catch (firestoreError) {
          console.error("Error saving to Firestore:", firestoreError);
          // If Firestore fails but upload succeeded, still return file info
          return {
            id: "temp-" + timestamp,
            filename: file.name,
            file_path: filePath,
            file_type: fileType,
            file_size: file.size,
            download_url: downloadURL
          };
        }
      } else {
        // For guest users, create a guest file
        try {
          const docRef = await addDoc(collection(db, "guestFiles"), fileData);
          fileId = docRef.id;
          console.log("Guest file metadata saved to Firestore");
        } catch (firestoreError) {
          console.error("Error saving guest file to Firestore:", firestoreError);
          // If Firestore fails but upload succeeded, still return file info
          return {
            id: "temp-" + timestamp,
            filename: file.name,
            file_path: filePath,
            file_type: fileType,
            file_size: file.size,
            download_url: downloadURL
          };
        }
      }
      
      // Return file data
      return {
        id: fileId,
        filename: file.name,
        file_path: filePath,
        file_type: fileType,
        file_size: file.size,
        download_url: downloadURL
      };
    } catch (uploadError) {
      console.error("Storage upload error:", uploadError);
      if (uploadError.code === 'storage/unauthorized') {
        throw new Error("Storage access denied. Please check your permissions.");
      } else if (uploadError.message && uploadError.message.includes('CORS')) {
        throw new Error("CORS error: Your browser is blocking the upload due to cross-origin restrictions. Please try using the deployed version of the app.");
      } else {
        throw new Error(`Storage error: ${uploadError.message}`);
      }
    }
  } catch (error) {
    console.error("File upload failed:", error);
    throw new Error(`Error uploading file: ${error.message}`);
  }
};

// Delete a file from Firebase Storage
export const deleteFile = async (filePath) => {
  try {
    const storageRef = ref(storage, filePath);
    await deleteObject(storageRef);
    return true;
  } catch (error) {
    throw new Error(`Error deleting file: ${error.message}`);
  }
};

// Helper function to determine file type from extension
const getFileType = (extension) => {
  const documentTypes = ['doc', 'docx', 'pdf', 'txt', 'rtf', 'odt'];
  const spreadsheetTypes = ['xls', 'xlsx', 'csv', 'ods'];
  const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'];
  
  if (documentTypes.includes(extension)) {
    return 'document';
  } else if (spreadsheetTypes.includes(extension)) {
    return 'spreadsheet';
  } else if (imageTypes.includes(extension)) {
    return 'image';
  } else {
    return 'other';
  }
}; 